import { GridColDef } from '@mui/x-data-grid-pro';
import { AppLog, AppLogSearch } from '../../../types';
import AvatarChip from '../../shared/avatar-chip';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import { handleErrors } from '../../utils/errors';
import { exportAppLogs } from '../../api/users-api';
import ChangeLogDialog from '../../shared/change-log-dialog';
import { AppLogSearchForm } from './logs-page';
import LogDetailsDialog from './log-details-dialog';
import AppLogGridToolbar from './app-log-grid-toolbar';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import { createGridOverlays } from '../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../shared/shared-data-grid-client';
import { useToolbar } from '../../shared/shared-data-grid-toolbar-helper';
import { useUserSettings } from '../../utils/use-user-settings';
import FindInPage from '@mui/icons-material/FindInPage';

interface AppLogSearchPageProps {
  changeLogRoles: string[];
  administrationRoles: string[];
  searchedLogs: AppLog[];
  isLoading: boolean;
  loggedInUser: string;
  appLogSearch: AppLogSearchForm;
}

export default function AppLogSearchGrid({
  changeLogRoles,
  searchedLogs,
  isLoading,
  administrationRoles,
  loggedInUser,
  appLogSearch,
}: AppLogSearchPageProps) {
  const { createEvent } = useAnalytics();
  const { viewType } = useUserSettings();
  const [selectedLog, setSelectedLog] = useState<AppLog | null>(null);
  const [openLogDetails, setOpenLogDetails] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);

  const { mutate, isPending: isExporting } = useMutation({
    mutationFn: (data: Partial<AppLogSearch>) =>
      exportAppLogs(data, changeLogMessage),
    onSuccess: (data) => {
      window.open(
        `https://docs.google.com/a/${
          loggedInUser.split('@')[1]
        }/spreadsheet/ccc?key=${data}#gid=0`,
        '_blank',
        'noopener, noreferrer'
      );
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue exporting the app log search results. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.logging.exported_app_logs, {
        module: ModuleList.modules.logging.name,
        section: ModuleList.modules.logging.sections.logging,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const handleExportMutate = useCallback(() => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.ExportAppLogs.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const newSearch: AppLogSearchForm = {
        ...appLogSearch,
        startedDate: appLogSearch.dateRange
          ? appLogSearch.dateRange[0]?.toDate().getTime()
          : 0,
        endDate: appLogSearch.dateRange
          ? appLogSearch.dateRange[1]?.toDate().getTime()
          : 0,
      };
      if (newSearch.performingUser?.length === 0) {
        delete newSearch.performingUser;
      }
      if (newSearch.target?.length === 0) {
        delete newSearch.target;
      }
      if (newSearch.permissionRequired?.length === 0) {
        delete newSearch.permissionRequired;
      }
      delete newSearch.dateRange;
      mutate(newSearch);
    }
  }, [appLogSearch, changeLogRoles, mutate]);

  const columns: GridColDef<AppLog>[] = [
    {
      field: 'createdDate',
      headerName: 'Created Date',
      editable: false,
      sortable: true,
      filterable: true,
      minWidth: 170,
      valueGetter: (_value, row) => {
        const date = new Date(row.createdDate);
        return date.toLocaleString();
      },
    },
    {
      field: 'performingUser',
      headerName: 'Performing User',
      editable: false,
      sortable: true,
      filterable: true,
      minWidth: 170,
      renderCell: (params) => {
        if (params.value) {
          return <AvatarChip email={params.value} type="User" />;
        }
      },
    },
    {
      field: 'permission',
      headerName: 'Permission',
      editable: false,
      sortable: true,
      filterable: true,
      minWidth: 200,
      valueGetter: (_value, row) => {
        if (row.permissionNiceName && row.permissionNiceName !== '') {
          return row.permissionNiceName;
        } else if (row.permission) {
          return row.permission;
        } else {
          return 'No Permission';
        }
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      editable: false,
      sortable: false,
      filterable: true,
      display: 'flex',
      flex: 1,
      valueGetter: (_value, row) => {
        if (row.action) {
          return row.action;
        } else {
          return 'No action was performed.';
        }
      },
    },
    {
      field: 'target',
      headerName: 'Target',
      editable: false,
      sortable: true,
      filterable: true,
      minWidth: 200,
      valueGetter: (_value, row) => {
        if (row.target) {
          return row.target;
        } else {
          return 'No Target';
        }
      },
    },
  ];

  const toolbarProps = useMemo(
    () => ({
      administrationRoles: administrationRoles,
      handleExportMutate,
      searchedLogs: searchedLogs,
      isExporting: isExporting,
      isSearching: isLoading,
    }),
    [
      administrationRoles,
      handleExportMutate,
      isExporting,
      isLoading,
      searchedLogs,
    ]
  );

  return (
    <>
      <SharedDataGridClient
        gridKey={viewType.loggingGridStates?.activeGridState}
        slots={{
          toolbar: useToolbar(AppLogGridToolbar, toolbarProps),
          ...createGridOverlays(
            !searchedLogs ? 'Search Above' : 'Logs',
            <FindInPage />,
            'Select any optional filters you would like to search by.',
            ' Try changing your search term.'
          ),
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'linear-progress',
          },
          filterPanel: {
            sx: {
              minWidth: 800,
            },
          },
        }}
        rows={searchedLogs}
        columns={columns}
        sx={{
          mt: 1,
        }}
        loading={isLoading}
        disableRowSelectionOnClick
        onRowClick={(row) => {
          const selectedLog = searchedLogs!.find(
            (log) => log.id === row.row.id
          )!;
          setSelectedLog(selectedLog);
          setOpenLogDetails(true);
        }}
        gridStateKey="loggingGridStates"
      />
      {openChangeLogDialog ? (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            const newSearch: AppLogSearchForm = {
              ...appLogSearch,
              startedDate: appLogSearch.dateRange
                ? appLogSearch.dateRange[0]?.toDate().getTime()
                : 0,
              endDate: appLogSearch.dateRange
                ? appLogSearch.dateRange[1]?.toDate().getTime()
                : 0,
            };
            if (newSearch.performingUser!.length === 0) {
              delete newSearch.performingUser;
            }
            if (newSearch.target!.length === 0) {
              delete newSearch.target;
            }
            if (newSearch.permissionRequired!.length === 0) {
              delete newSearch.permissionRequired;
            }
            delete newSearch.dateRange;
            mutate(newSearch);
          }}
          mutateLoading={isExporting}
        />
      ) : null}
      {selectedLog && openLogDetails ? (
        <LogDetailsDialog
          open={openLogDetails}
          handleClose={() => setOpenLogDetails(false)}
          selectedLog={selectedLog}
        />
      ) : null}
    </>
  );
}
